import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Bell, Loader2, ArrowLeft, MoreHorizontal, History
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { auth } from '../../utils/auth';
import { cn } from '../../lib/utils';
import { format, parseISO, differenceInDays } from 'date-fns';
import { enUS, nb } from 'date-fns/locale';
import CaseTimelineModal from './CaseTimelineModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * CASE STATUS HEADER - Compact header bar
 * Shows: Back button | Case title | Status | Risk | Deadline | New items
 */
const CaseStatusHeader = ({ caseId, caseName, isExpanded, onToggleExpand, onSwitchCase }) => {
  const { t, i18n } = useTranslation(['cases', 'common']);
  const dateLocale = i18n.language === 'nb' ? nb : enUS;
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newSinceLast, setNewSinceLast] = useState([]);
  const [showTimeline, setShowTimeline] = useState(false);

  // Fetch case data
  const fetchCaseData = useCallback(async () => {
    if (!caseId) return;
    
    try {
      const token = auth.getToken();
      const headers = { Authorization: `Bearer ${token}` };
      
      const caseRes = await axios.get(`${API}/cases/${caseId}`, { headers });
      setCaseData(caseRes.data);
      
      // Check for new items since last visit
      const lastViewed = localStorage.getItem(`case_last_viewed_${caseId}`);
      try {
        const sinceParam = lastViewed ? `?since=${lastViewed}` : '';
        const response = await axios.get(`${API}/cases/${caseId}/whats-new${sinceParam}`, { headers });
        const data = response.data;
        const newItems = [];
        
        if (data.summary?.emails_count > 0) newItems.push({ type: 'emails', count: data.summary.emails_count });
        if (data.summary?.documents_count > 0) newItems.push({ type: 'documents', count: data.summary.documents_count });
        if (data.summary?.tasks_count > 0) newItems.push({ type: 'tasks', count: data.summary.tasks_count });
        
        setNewSinceLast(newItems);
      } catch (e) {
        setNewSinceLast([]);
      }
      
      // Update last viewed
      localStorage.setItem(`case_last_viewed_${caseId}`, new Date().toISOString());
      
    } catch (error) {
      console.error('Error fetching case data:', error);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchCaseData();
  }, [fetchCaseData]);

  // Risk styling
  const getRiskStyling = (risk) => {
    const r = (risk || 'medium').toLowerCase();
    if (r === 'high' || r === 'høy') return { dot: 'bg-red-500', label: 'Høy' };
    if (r === 'low' || r === 'lav') return { dot: 'bg-emerald-500', label: 'Lav' };
    return { dot: 'bg-amber-500', label: 'Middels' };
  };

  // Status styling - Minimalist monochrome design
  const getStatusStyling = (status: string | undefined) => {
    const s = (status || '').toLowerCase().replace(/_/g, ' ');
    
    // Map database values to user-friendly labels
    if (s.includes('in progress') || s.includes('in_progress') || s.includes('pågår')) {
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'In Progress', key: 'inProgress' };
    }
    if (s.includes('active') || s.includes('aktiv')) {
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Active', key: 'active' };
    }
    if (s.includes('pending') || s.includes('venter') || s.includes('review')) {
      return { bg: 'bg-gray-100', text: 'text-gray-500', label: 'Pending', key: 'pending' };
    }
    if (s.includes('closed') || s.includes('lukket') || s.includes('completed')) {
      return { bg: 'bg-gray-50', text: 'text-gray-400', label: 'Closed', key: 'closed' };
    }
    if (s.includes('new') || s.includes('ny')) {
      return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'New', key: 'new' };
    }
    // Default - show "Open" for any unknown status
    return { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Open', key: 'open' };
  };

  // Format deadline
  const formatDeadline = (deadline: string | undefined) => {
    if (!deadline) return null;
    try {
      const date = parseISO(deadline);
      const daysUntil = differenceInDays(date, new Date());
      if (daysUntil < 0) return { label: 'Forfalt', urgent: true };
      if (daysUntil === 0) return { label: 'I dag', urgent: true };
      if (daysUntil <= 3) return { label: `${daysUntil}d`, urgent: true };
      return { label: format(date, 'd. MMM', { locale: nb }), urgent: false };
    } catch (e) {
      return null;
    }
  };

  if (loading) {
    return (
      <div className="h-10 border-b border-gray-200 bg-white px-3 flex items-center flex-shrink-0">
        <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />
        <span className="text-xs text-gray-500 ml-2">{t('common:buttons.loading')}</span>
      </div>
    );
  }

  if (!caseData) return null;

  const risk = getRiskStyling(caseData.risk_level || caseData.ai_analysis?.risk_level);
  const status = getStatusStyling(caseData.status);
  const deadline = formatDeadline(caseData.next_deadline || caseData.deadline);
  const totalNew = newSinceLast.reduce((sum, item) => sum + item.count, 0);

  // Navigate to case detail page
  const handleCaseClick = () => {
    if (caseData.id) {
      navigate(`/lexy/case/${caseData.id}`);
    }
  };

  return (
    <>
      <div className="h-12 border-b border-gray-100 bg-white flex items-center px-4 gap-3 flex-shrink-0">
        {/* Back button - prominent */}
        <button
          onClick={onSwitchCase || (() => navigate('/lexy/dashboard?tab=case-queue'))}
          className="p-2 -ml-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title={t('common:buttons.back')}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        
        {/* Case title - clickable to open case */}
        <h2 
          onClick={handleCaseClick}
          className="text-sm font-semibold text-gray-900 truncate flex-1 min-w-0 cursor-pointer hover:text-blue-600 transition-colors" 
          title={`${caseData.title || caseName} - Click to open case`}
        >
          {caseData.title || caseName}
        </h2>
        
        {/* Status badge */}
        <span className={cn("px-2 py-1 rounded text-xs font-medium flex-shrink-0", status.bg, status.text)}>
          {String(t(`cases:status.${status.key}`, status.label))}
        </span>
        
        {/* Risk indicator */}
        <div className={cn("w-2 h-2 rounded-full flex-shrink-0", risk.dot)} title={`${t('common:labels.risk', 'Risk')}: ${risk.label}`} />
        
        {/* Deadline if urgent */}
        {deadline?.urgent && (
          <span className="text-xs font-medium text-red-600 flex items-center gap-1 flex-shrink-0">
            <Clock className="w-3.5 h-3.5" />
            {deadline.label}
          </span>
        )}
        
        {/* New items - Monochrome */}
        {totalNew > 0 && (
          <div className="flex items-center gap-1 px-2 py-1 bg-gray-100 rounded text-xs text-gray-600 flex-shrink-0">
            <Bell className="w-3.5 h-3.5" />
            <span>{totalNew}</span>
          </div>
        )}
        
        {/* More options */}
        <button
          onClick={onToggleExpand}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title="Vis mer"
        >
          <MoreHorizontal className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Expanded section */}
      {isExpanded && (
        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100 flex-shrink-0">
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div>
              <span className="text-gray-500">Fase</span>
              <p className="font-medium text-gray-900">{caseData.current_phase || 'Vurdering'}</p>
            </div>
            <div>
              <span className="text-gray-500">Timer</span>
              <p className="font-medium text-gray-900">{(caseData.hours_logged || 0).toFixed(1)}t</p>
            </div>
            <div>
              <span className="text-gray-500">Risiko</span>
              <p className="font-medium text-gray-900">{risk.label}</p>
            </div>
            <div>
              <span className="text-gray-500">Verdi</span>
              <p className="font-medium text-gray-900">
                {caseData.estimated_value > 0 ? `${caseData.estimated_value.toLocaleString('nb-NO')} kr` : '-'}
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowTimeline(true)}
            className="mt-2 flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 rounded transition-colors"
          >
            <History className="w-3 h-3" />
            Historikk
          </button>
        </div>
      )}
      
      <CaseTimelineModal
        isOpen={showTimeline}
        onClose={() => setShowTimeline(false)}
        caseId={caseId}
        caseName={caseData?.title || caseName}
      />
    </>
  );
};

export default CaseStatusHeader;
